import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { crmDataService } from "./utils/crmDataService";
import ConditionalFieldManager from "./components/ConditionalFieldManager";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import "./styles/crmSettings.css";

function CRMSettings({ onBack }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("teams");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Teams
  const [teams, setTeams] = useState([]);
  const [editingTeam, setEditingTeam] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamForm, setTeamForm] = useState({
    code: "",
    name: "",
    description: "",
  });

  // Categories
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    teamId: "",
    name: "",
    description: "",
  });

  // Subcategories
  const [subcategories, setSubcategories] = useState([]);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [subcategoryForm, setSubcategoryForm] = useState({
    categoryId: "",
    name: "",
    description: "",
    slaHours: 24,
  });

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null,
    id: null,
    name: null,
  });

  // Approval Configuration
  const [approvalConfigs, setApprovalConfigs] = useState([]);
  const [approvalRoles, setApprovalRoles] = useState([]);
  const [editingApproval, setEditingApproval] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    subcategoryId: "",
    requiresApproval: true,
    approver1Role: "",
    approver2Role: "",
    approver3Role: "",
    approver4Role: "",
  });
  const [selectedTeamForApproval, setSelectedTeamForApproval] = useState("");
  const [selectedCategoryForApproval, setSelectedCategoryForApproval] =
    useState("");
  const [categoriesForApproval, setCategoriesForApproval] = useState([]);
  const [subcategoriesForApproval, setSubcategoriesForApproval] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      if (activeTab === "teams") {
        const data = await crmDataService.getTeams();
        setTeams(data || []);
      } else if (activeTab === "categories") {
        const [teamsData, hierarchyData] = await Promise.all([
          crmDataService.getTeams(),
          crmDataService.getDropdownData(),
        ]);

        const allCategories = [];
        if (hierarchyData && hierarchyData?.length) {
          const teamMap = {};
          hierarchyData.forEach((team) => {
            teamMap[team.id] = team.name;
          });

          hierarchyData.forEach((team) => {
            const categories = team.children;
            if (Array.isArray(categories)) {
              categories.forEach((category) => {
                allCategories.push({
                  ...category,
                  team_id: team.id,
                  team_name: team.name || "Unknown Team",
                });
              });
            }
          });
        }

        setTeams(teamsData || []);
        setCategories(allCategories);
      } else if (activeTab === "subcategories") {
        const hierarchyData = await crmDataService.getDropdownData();

        const allCategories = [];
        const allSubcategories = [];

        if (hierarchyData) {
          const teamMap = {};
          const categoryMap = {};

          hierarchyData.forEach((team) => {
            teamMap[team.id] = team.name;
          });

          if (hierarchyData) {
            hierarchyData.forEach((team) => {
              const categories = team.children;
              if (Array.isArray(categories)) {
                categories.forEach((category) => {
                  categoryMap[category.id] = category.name;
                  allCategories.push({
                    ...category,
                    team_id: team.id,
                    team_name: teamMap[team.id] || "Unknown Team",
                  });
                });
              }
            });
          }

          hierarchyData.forEach((team) => {
            team.children.forEach((category) => {
              const subcategories = category.children;
              if (Array.isArray(subcategories)) {
                subcategories.forEach((subcategory) => {
                  allSubcategories.push({
                    ...subcategory,
                    category_id: category.id,
                    category_name:
                      categoryMap[category.id] || "Unknown Category",
                  });
                });
              }
            });
          });
        }

        setCategories(allCategories);
        setSubcategories(allSubcategories);
      } else if (activeTab === "approvals") {
        const [configsData, rolesData, teamsData, hierarchyData] =
          await Promise.all([
            crmDataService.getApprovalConfigs(),
            crmDataService.getApprovalRoles(),
            crmDataService.getTeams(),
            crmDataService.getDropdownData(),
          ]);

        setApprovalConfigs(configsData || []);
        setApprovalRoles(rolesData || []);
        setTeams(teamsData || []);

        const allCategories = [];
        const allSubcategories = [];

        if (hierarchyData) {
          hierarchyData.forEach((team) => {
            const categories = team.children;
            if (Array.isArray(categories)) {
              categories.forEach((category) => {
                allCategories.push({
                  ...category,
                  team_id: team.id,
                  team_name: team.name || "Unknown Team",
                });

                const subcategories = category.children;
                if (Array.isArray(subcategories)) {
                  subcategories.forEach((subcategory) => {
                    allSubcategories.push({
                      ...subcategory,
                      category_id: category.id,
                      category_name: category.name,
                    });
                  });
                }
              });
            }
          });
        }

        setCategories(allCategories);
        setSubcategories(allSubcategories);
      }

      setError(null);
    } catch (err) {
      console.error("Failed to load data:", err);
      setError("Failed to load configuration data");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Team Management
  const handleSaveTeam = async (e) => {
    e.preventDefault();
    try {
      if (editingTeam) {
        await crmDataService.updateTeam(editingTeam.id, {
          team_code: teamForm.code,
          team_name: teamForm.name,
          description: teamForm.description,
        });
        setSuccessMessage("Team updated successfully");
      } else {
        await crmDataService.createTeam({
          team_code: teamForm.code,
          team_name: teamForm.name,
          description: teamForm.description,
        });
        setSuccessMessage("Team created successfully");
      }

      setShowTeamModal(false);
      setEditingTeam(null);
      setTeamForm({ code: "", name: "", description: "" });
      loadData();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError("Failed to save team: " + err.message);
    }
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setTeamForm({
      code: team.code,
      name: team.name,
      description: team.description || "",
    });
    setShowTeamModal(true);
  };

  const handleDeleteTeam = (team) => {
    setDeleteModal({
      isOpen: true,
      type: "team",
      id: team.id,
      name: team.name,
    });
  };

  // Category Management
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await crmDataService.updateCategory(editingCategory.id, categoryForm);
        setSuccessMessage("Category updated successfully");
      } else {
        await crmDataService.createCategory(categoryForm);
        setSuccessMessage("Category created successfully");
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ teamId: "", name: "", description: "" });
      loadData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError("Failed to save category: " + err.message);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      teamId: category.team_id,
      name: category.name,
      description: category.description || "",
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = (category) => {
    setDeleteModal({
      isOpen: true,
      type: "category",
      id: category.id,
      name: category.name,
    });
  };

  // Subcategory Management
  const handleSaveSubcategory = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await crmDataService.updateSubcategory(
          editingSubcategory.id,
          subcategoryForm
        );
        setSuccessMessage("Subcategory updated successfully");
      } else {
        await crmDataService.createSubcategory(subcategoryForm);
        setSuccessMessage("Subcategory created successfully");
      }
      setShowSubcategoryModal(false);
      setEditingSubcategory(null);
      setSubcategoryForm({
        categoryId: "",
        name: "",
        description: "",
        slaHours: 24,
      });
      loadData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError("Failed to save subcategory: " + err.message);
    }
  };

  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      categoryId: subcategory.category_id,
      name: subcategory.name,
      description: subcategory.description || "",
      slaHours: subcategory.sla_hours || 24,
    });
    setShowSubcategoryModal(true);
  };

  const handleDeleteSubcategory = (subcategory) => {
    setDeleteModal({
      isOpen: true,
      type: "subcategory",
      id: subcategory.id,
      name: subcategory.name,
    });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteModal;
    if (!type || !id) return;

    try {
      if (type === "team") {
        await crmDataService.deleteTeam(id);
        setSuccessMessage("Team deleted successfully");
      } else if (type === "category") {
        await crmDataService.deleteCategory(id);
        setSuccessMessage("Category deleted successfully");
      } else if (type === "subcategory") {
        await crmDataService.deleteSubcategory(id);
        setSuccessMessage("Subcategory deleted successfully");
      } else if (type === "approval") {
        await crmDataService.deleteApprovalConfig(id);
        setSuccessMessage("Approval configuration deleted successfully");
      }

      loadData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(`Failed to delete ${type}: ` + err.message);
    } finally {
      setDeleteModal({ isOpen: false, type: null, id: null, name: null });
    }
  };

  // Approval Configuration Management
  const handleTeamChangeForApproval = (teamId) => {
    setSelectedTeamForApproval(teamId);
    setSelectedCategoryForApproval("");
    setApprovalForm({ ...approvalForm, subcategoryId: "" });

    const filtered = categories.filter(
      (cat) => cat.team_id === parseInt(teamId)
    );
    setCategoriesForApproval(filtered);
    setSubcategoriesForApproval([]);
  };

  const handleCategoryChangeForApproval = (categoryId) => {
    setSelectedCategoryForApproval(categoryId);
    setApprovalForm({ ...approvalForm, subcategoryId: "" });

    const filtered = subcategories.filter(
      (sub) => sub.category_id === parseInt(categoryId)
    );
    setSubcategoriesForApproval(filtered);
  };

  const handleSaveApproval = async (e) => {
    e.preventDefault();
    try {
      await crmDataService.saveApprovalConfig(approvalForm);
      setSuccessMessage(
        editingApproval
          ? "Approval configuration updated successfully"
          : "Approval configuration created successfully"
      );
      setShowApprovalModal(false);
      setEditingApproval(null);
      setApprovalForm({
        subcategoryId: "",
        requiresApproval: true,
        approver1Role: "",
        approver2Role: "",
        approver3Role: "",
        approver4Role: "",
      });
      setSelectedTeamForApproval("");
      setSelectedCategoryForApproval("");
      setCategoriesForApproval([]);
      setSubcategoriesForApproval([]);
      loadData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError("Failed to save approval configuration: " + err.message);
    }
  };

  const handleEditApproval = (config) => {
    setEditingApproval(config);

    const category = categories.find((cat) => cat.id === config.category_id);

    if (category) {
      setSelectedTeamForApproval(category.team_id?.toString() || "");
      const filteredCats = categories.filter(
        (cat) => cat.team_id === category.team_id
      );
      setCategoriesForApproval(filteredCats);
      setSelectedCategoryForApproval(config.category_id?.toString() || "");
      const filteredSubs = subcategories.filter(
        (sub) => sub.category_id === config.category_id
      );
      setSubcategoriesForApproval(filteredSubs);
    }

    setApprovalForm({
      subcategoryId: config.subcategory_id?.toString() || "",
      requiresApproval: config.requires_approval !== false,
      approver1Role: config.approver_1_role || "",
      approver2Role: config.approver_2_role || "",
      approver3Role: config.approver_3_role || "",
      approver4Role: config.approver_4_role || "",
    });
    setShowApprovalModal(true);
  };

  const handleDeleteApproval = (config) => {
    setDeleteModal({
      isOpen: true,
      type: "approval",
      id: config.subcategory_id,
      name: `${config.subcategory_name} approval configuration`,
    });
  };

  const getRoleLabel = (roleKey) => {
    const role = approvalRoles.find((r) => r.key === roleKey);
    return role ? role.label : roleKey || "-";
  };

  const handleBackToSettings = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/settings/hr");
    }
  };

  return (
    <div className="crm-dashboard-container crm-settings">
      {/* Header with Back Button */}
      <div className="crm-dashboard-header">
        <h1>CRM Settings</h1>
        <button className="btn-back" onClick={handleBackToSettings}>
          <span className="back-arrow">←</span>
          Back to HR Settings
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      <div className="crm-tabs">
        <button
          className={activeTab === "teams" ? "active" : ""}
          onClick={() => setActiveTab("teams")}
        >
          Teams
        </button>
        <button
          className={activeTab === "categories" ? "active" : ""}
          onClick={() => setActiveTab("categories")}
        >
          Categories
        </button>
        <button
          className={activeTab === "subcategories" ? "active" : ""}
          onClick={() => setActiveTab("subcategories")}
        >
          Subcategories
        </button>
        <button
          className={activeTab === "fields" ? "active" : ""}
          onClick={() => setActiveTab("fields")}
        >
          Conditional Fields
        </button>
        <button
          className={activeTab === "approvals" ? "active" : ""}
          onClick={() => setActiveTab("approvals")}
        >
          Approvals
        </button>
{/* Hidden for now - SLA Rules tab
        <button
          className={activeTab === "sla" ? "active" : ""}
          onClick={() => setActiveTab("sla")}
        >
          SLA Rules
        </button>
*/}
      </div>

      <div className="settings-content">
        {/* Teams Tab */}
        {activeTab === "teams" && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Teams</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingTeam(null);
                  setTeamForm({ code: "", name: "", description: "" });
                  setShowTeamModal(true);
                }}
              >
                + Add Team
              </button>
            </div>

            {loading ? (
              <div className="loading-spinner"></div>
            ) : (
              <div className="table-responsive">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="empty-state">
                          No teams found
                        </td>
                      </tr>
                    ) : (
                      teams.map((team) => (
                        <tr key={team.id}>
                          <td>
                            <code>{team.code}</code>
                          </td>
                          <td>{team.name}</td>
                          <td>{team.description || "N/A"}</td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={() => handleEditTeam(team)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-link text-danger"
                              onClick={() => handleDeleteTeam(team)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === "categories" && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Categories</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryForm({ teamId: "", name: "", description: "" });
                  setShowCategoryModal(true);
                }}
              >
                + Add Category
              </button>
            </div>

            {loading ? (
              <div className="loading-spinner"></div>
            ) : (
              <div className="table-responsive">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Team</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="empty-state">
                          No categories found
                        </td>
                      </tr>
                    ) : (
                      categories.map((category) => (
                        <tr key={category.id}>
                          <td>{category.name}</td>
                          <td>{category.team_name || "N/A"}</td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={() => handleEditCategory(category)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-link text-danger"
                              onClick={() => handleDeleteCategory(category)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Subcategories Tab */}
        {activeTab === "subcategories" && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Subcategories</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingSubcategory(null);
                  setSubcategoryForm({
                    categoryId: "",
                    name: "",
                    description: "",
                    slaHours: 24,
                  });
                  setShowSubcategoryModal(true);
                }}
              >
                + Add Subcategory
              </button>
            </div>

            {loading ? (
              <div className="loading-spinner"></div>
            ) : (
              <div className="table-responsive">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th>SLA (hours)</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subcategories.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="empty-state">
                          No subcategories found
                        </td>
                      </tr>
                    ) : (
                      subcategories.map((subcat) => (
                        <tr key={subcat.id}>
                          <td>{subcat.name}</td>
                          <td>{subcat.category_name || "N/A"}</td>
                          <td>{subcat.sla_hours || 24}</td>
                          <td>{subcat.description || "N/A"}</td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={() => handleEditSubcategory(subcat)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-link text-danger"
                              onClick={() => handleDeleteSubcategory(subcat)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Conditional Fields Tab */}
        {activeTab === "fields" && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Conditional Fields</h2>
            </div>
            <p className="info-message">
              Conditional fields allow you to collect additional information
              based on ticket category/subcategory.
            </p>
            <ConditionalFieldManager />
          </div>
        )}

        {/* Approvals Tab */}
        {activeTab === "approvals" && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Approval Configurations</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingApproval(null);
                  setApprovalForm({
                    subcategoryId: "",
                    requiresApproval: true,
                    approver1Role: "",
                    approver2Role: "",
                    approver3Role: "",
                    approver4Role: "",
                  });
                  setSelectedTeamForApproval("");
                  setSelectedCategoryForApproval("");
                  setCategoriesForApproval([]);
                  setSubcategoriesForApproval([]);
                  setShowApprovalModal(true);
                }}
              >
                + Add Approval Config
              </button>
            </div>
            <p className="info-message">
              Configure approval chains for specific subcategories. Approvers
              are determined by the ticket's store hierarchy roles.
            </p>

            {loading ? (
              <div className="loading-spinner"></div>
            ) : (
              <div className="table-responsive">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Category</th>
                      <th>Subcategory</th>
                      <th>Approver 1</th>
                      <th>Approver 2</th>
                      <th>Approver 3</th>
                      <th>Approver 4</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalConfigs.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="empty-state">
                          No approval configurations found
                        </td>
                      </tr>
                    ) : (
                      approvalConfigs.map((config) => (
                        <tr key={config.id}>
                          <td>{config.team_name || "N/A"}</td>
                          <td>{config.category_name || "N/A"}</td>
                          <td>{config.subcategory_name || "N/A"}</td>
                          <td>{getRoleLabel(config.approver_1_role)}</td>
                          <td>{getRoleLabel(config.approver_2_role)}</td>
                          <td>{getRoleLabel(config.approver_3_role)}</td>
                          <td>{getRoleLabel(config.approver_4_role)}</td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={() => handleEditApproval(config)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-link text-danger"
                              onClick={() => handleDeleteApproval(config)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

{/* Hidden for now - SLA Tab
        {activeTab === "sla" && (
          <div className="settings-section">
            <div className="section-header">
              <h2>SLA Rules</h2>
              <button className="btn-primary">+ Add SLA Rule</button>
            </div>
            <p className="info-message">
              Define Service Level Agreement rules based on priority and team.
            </p>
            <div className="empty-state">SLA configuration coming soon...</div>
          </div>
        )}
*/}

        {/* Assignment Rules Tab */}
        {activeTab === "assignment" && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Assignment Rules</h2>
              <button className="btn-primary">+ Add Rule</button>
            </div>
            <p className="info-message">
              Configure automatic ticket assignment rules based on team,
              category, and store.
            </p>
            <div className="empty-state">
              Assignment rule configuration coming soon...
            </div>
          </div>
        )}
      </div>

      {/* Team Modal */}
      {showTeamModal && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTeam ? "Edit Team" : "Add New Team"}</h2>
              <button onClick={() => setShowTeamModal(false)}>×</button>
            </div>

            <form onSubmit={handleSaveTeam}>
              <div className="form-group">
                <label>Team Code *</label>
                <input
                  type="text"
                  value={teamForm.code}
                  onChange={(e) =>
                    setTeamForm({ ...teamForm, code: e.target.value })
                  }
                  required
                  placeholder="e.g., HR, IT, OPS"
                />
              </div>

              <div className="form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={teamForm.name}
                  onChange={(e) =>
                    setTeamForm({ ...teamForm, name: e.target.value })
                  }
                  required
                  placeholder="e.g., Human Resources"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={teamForm.description}
                  onChange={(e) =>
                    setTeamForm({ ...teamForm, description: e.target.value })
                  }
                  rows="3"
                  placeholder="Team description..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowTeamModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTeam ? "Update" : "Create"} Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCategoryModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? "Edit Category" : "Add New Category"}</h2>
              <button onClick={() => setShowCategoryModal(false)}>×</button>
            </div>

            <form onSubmit={handleSaveCategory}>
              <div className="form-group">
                <label>Team *</label>
                <select
                  value={categoryForm.teamId}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, teamId: e.target.value })
                  }
                  required
                >
                  <option value="">Select Team</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  required
                  placeholder="e.g., Payroll, Benefits"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCategoryModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingCategory ? "Update" : "Create"} Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSubcategoryModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingSubcategory
                  ? "Edit Subcategory"
                  : "Add New Subcategory"}
              </h2>
              <button onClick={() => setShowSubcategoryModal(false)}>×</button>
            </div>

            <form onSubmit={handleSaveSubcategory}>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={subcategoryForm.categoryId}
                  onChange={(e) =>
                    setSubcategoryForm({
                      ...subcategoryForm,
                      categoryId: e.target.value,
                    })
                  }
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subcategory Name *</label>
                <input
                  type="text"
                  value={subcategoryForm.name}
                  onChange={(e) =>
                    setSubcategoryForm({
                      ...subcategoryForm,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>SLA (hours) *</label>
                <input
                  type="number"
                  value={subcategoryForm.slaHours}
                  onChange={(e) =>
                    setSubcategoryForm({
                      ...subcategoryForm,
                      slaHours: e.target.value,
                    })
                  }
                  required
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={subcategoryForm.description}
                  onChange={(e) =>
                    setSubcategoryForm({
                      ...subcategoryForm,
                      description: e.target.value,
                    })
                  }
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowSubcategoryModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingSubcategory ? "Update" : "Create"} Subcategory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Configuration Modal */}
      {showApprovalModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowApprovalModal(false)}
        >
          <div
            className="modal-content modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {editingApproval
                  ? "Edit Approval Configuration"
                  : "Add New Approval Configuration"}
              </h2>
              <button onClick={() => setShowApprovalModal(false)}>×</button>
            </div>

            <form onSubmit={handleSaveApproval}>
              <div className="form-row">
                <div className="form-group">
                  <label>Team *</label>
                  <select
                    value={selectedTeamForApproval}
                    onChange={(e) =>
                      handleTeamChangeForApproval(e.target.value)
                    }
                    required
                    disabled={editingApproval}
                  >
                    <option value="">Select Team</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={selectedCategoryForApproval}
                    onChange={(e) =>
                      handleCategoryChangeForApproval(e.target.value)
                    }
                    required
                    disabled={editingApproval || !selectedTeamForApproval}
                  >
                    <option value="">Select Category</option>
                    {categoriesForApproval.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Subcategory *</label>
                  <select
                    value={approvalForm.subcategoryId}
                    onChange={(e) =>
                      setApprovalForm({
                        ...approvalForm,
                        subcategoryId: e.target.value,
                      })
                    }
                    required
                    disabled={editingApproval || !selectedCategoryForApproval}
                  >
                    <option value="">Select Subcategory</option>
                    {subcategoriesForApproval.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={approvalForm.requiresApproval}
                    onChange={(e) =>
                      setApprovalForm({
                        ...approvalForm,
                        requiresApproval: e.target.checked,
                      })
                    }
                  />
                  <span>Requires Approval</span>
                </label>
              </div>

              {approvalForm.requiresApproval && (
                <div className="approval-roles-section">
                  <h4>Approval Chain (in order)</h4>
                  <p className="help-text">
                    Select the roles that will approve tickets for this
                    subcategory. Leave empty for no approval at that level.
                  </p>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Approver 1 *</label>
                      <select
                        value={approvalForm.approver1Role}
                        onChange={(e) =>
                          setApprovalForm({
                            ...approvalForm,
                            approver1Role: e.target.value,
                          })
                        }
                        required={approvalForm.requiresApproval}
                      >
                        <option value="">Select Role</option>
                        {approvalRoles.map((role) => (
                          <option key={role.key} value={role.key}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Approver 2</label>
                      <select
                        value={approvalForm.approver2Role}
                        onChange={(e) =>
                          setApprovalForm({
                            ...approvalForm,
                            approver2Role: e.target.value,
                          })
                        }
                      >
                        <option value="">None</option>
                        {approvalRoles.map((role) => (
                          <option key={role.key} value={role.key}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Approver 3</label>
                      <select
                        value={approvalForm.approver3Role}
                        onChange={(e) =>
                          setApprovalForm({
                            ...approvalForm,
                            approver3Role: e.target.value,
                          })
                        }
                      >
                        <option value="">None</option>
                        {approvalRoles.map((role) => (
                          <option key={role.key} value={role.key}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Approver 4</label>
                      <select
                        value={approvalForm.approver4Role}
                        onChange={(e) =>
                          setApprovalForm({
                            ...approvalForm,
                            approver4Role: e.target.value,
                          })
                        }
                      >
                        <option value="">None</option>
                        {approvalRoles.map((role) => (
                          <option key={role.key} value={role.key}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowApprovalModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingApproval ? "Update" : "Create"} Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
        onConfirm={handleConfirmDelete}
        itemName={deleteModal.name}
      />
    </div>
  );
}

export default CRMSettings;
